/**
 * Comment Mode Provider - Cross-Domain Comment System
 *
 * This provider manages global comment mode state and handles:
 * - Comment mode activation/deactivation (toggle state)
 * - X/Y coordinate tracking for comment pin placement
 * - URL-scoped comment pin storage and loading
 * - Click event handling when mode is active
 * - ESC key to cancel mode
 *
 * Design Pattern:
 * - URL + X/Y coordinates = comment scope
 * - Comments automatically scoped by current page URL
 * - FloatingControls toggle triggers comment placement mode
 * - Click anywhere to place comment pin
 *
 * Reference: .claude/plans/comment-infra-shared-migration.md
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthUnified } from '@/domains/billing/hooks/shared/useAuthUnified';
import { CommentPinService } from '@/shared/services/commentPinService';

// Comment Pin data structure
export interface CommentPin {
  id: string;              // Unique ID
  x: number;               // X coordinate (viewport relative)
  y: number;               // Y coordinate (viewport relative)
  scrollY: number;         // Scroll position when created
  pageUrl: string;         // Full URL path (scope)
  domain: string;          // Domain for context
  createdAt: string;       // ISO timestamp
  createdBy: string;       // Employee ID
  viewportWidth: number;   // Viewport width for responsive handling
  viewportHeight: number;  // Viewport height for responsive handling
  resolved?: boolean;      // Whether the comment is resolved
}

interface CommentModeContextState {
  // Mode state
  isCommentModeActive: boolean;
  activateCommentMode: () => void;
  deactivateCommentMode: () => void;
  toggleCommentMode: () => void;

  // Pin management
  commentPins: CommentPin[];
  addCommentPin: (x: number, y: number) => Promise<CommentPin>;
  removeCommentPin: (pinId: string) => Promise<void>;
  resolveCommentPin: (pinId: string) => Promise<void>;

  // Active pin (currently open modal)
  activePinId: string | null;
  setActivePinId: (pinId: string | null) => void;
}

const CommentModeContext = createContext<CommentModeContextState | undefined>(undefined);

interface CommentModeProviderProps {
  children: ReactNode;
}

/**
 * Comment Mode Provider
 *
 * Manages comment mode state and pin placement for the entire app.
 * Automatically loads pins for the current page URL.
 */
export const CommentModeProvider: React.FC<CommentModeProviderProps> = ({ children }) => {
  const location = useLocation();
  const { effectiveUser } = useAuthUnified();

  const [isCommentModeActive, setIsCommentModeActive] = useState(false);
  const [commentPins, setCommentPins] = useState<CommentPin[]>([]);
  const [activePinId, setActivePinId] = useState<string | null>(null);

  // Ref to track if we're currently handling a click
  const isHandlingClick = useRef(false);

  // Get current domain from URL
  const getCurrentDomain = useCallback((): string => {
    const segments = location.pathname.replace(/^\//, '').split('/');
    return segments[0] || 'unknown';
  }, [location.pathname]);

  // Subscribe to pins for current page with real-time updates
  useEffect(() => {
    const unsubscribe = CommentPinService.subscribeToPagePins(
      location.pathname,
      (pins) => setCommentPins(pins)
    );
    return () => unsubscribe();
  }, [location.pathname]);

  // Activate comment mode
  const activateCommentMode = useCallback(() => {
    setIsCommentModeActive(true);
    // Change cursor to crosshair
    document.body.style.cursor = 'crosshair';
  }, []);

  // Deactivate comment mode
  const deactivateCommentMode = useCallback(() => {
    setIsCommentModeActive(false);
    // Reset cursor
    document.body.style.cursor = '';
  }, []);

  // Toggle comment mode
  const toggleCommentMode = useCallback(() => {
    if (isCommentModeActive) {
      deactivateCommentMode();
    } else {
      activateCommentMode();
    }
  }, [isCommentModeActive, activateCommentMode, deactivateCommentMode]);

  // Add comment pin at coordinates
  const addCommentPin = useCallback(async (x: number, y: number, scrollYParam?: number): Promise<CommentPin> => {
    if (!effectiveUser?.employee_id) {
      throw new Error('User not authenticated');
    }

    const actualScrollY = typeof scrollYParam === 'number' ? scrollYParam : window.scrollY;

    const pin: CommentPin = {
      id: `pin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      x,
      y,
      scrollY: actualScrollY,
      pageUrl: location.pathname,
      domain: getCurrentDomain(),
      createdAt: new Date().toISOString(),
      createdBy: effectiveUser.employee_id.toString(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };

    // Save to Firestore (onSnapshot subscription delivers pin to local state)
    await CommentPinService.savePin(pin);

    // Deactivate comment mode after placing pin
    deactivateCommentMode();

    // Set as active pin to open modal
    setActivePinId(pin.id);

    return pin;
  }, [effectiveUser, location.pathname, getCurrentDomain, deactivateCommentMode]);

  // Remove comment pin
  const removeCommentPin = useCallback(async (pinId: string) => {
    // Delete from Firestore
    await CommentPinService.deletePin(pinId);

    // Remove from local state (functional updater avoids stale closure over commentPins)
    setCommentPins(prev => prev.filter(p => p.id !== pinId));

    // Clear active pin if it was the one removed
    setActivePinId(current => current === pinId ? null : current);

    // Clean up localStorage draft for this pin
    const draftKey = `comment-draft-${pinId}`;
    localStorage.removeItem(draftKey);
    localStorage.removeItem(`${draftKey}-mentions`);
  }, []);

  // Resolve comment pin
  const resolveCommentPin = useCallback(async (pinId: string) => {
    // Update in Firestore
    await CommentPinService.resolvePin(pinId);

    // Remove from local state immediately (subscription confirms via resolved filter)
    setCommentPins(pins => pins.filter(p => p.id !== pinId));

    // Clear active pin if it was the one resolved
    setActivePinId(current => current === pinId ? null : current);
  }, []);

  // Handle clicks when comment mode is active
  useEffect(() => {
    if (!isCommentModeActive) return;

    const handleClick = async (event: MouseEvent) => {
      // Prevent duplicate handling
      if (isHandlingClick.current) return;
      isHandlingClick.current = true;

      try {
        // Don't place pins on UI elements (buttons, links, inputs, etc.)
        const target = event.target as HTMLElement;
        if (
          target.tagName === 'BUTTON' ||
          target.tagName === 'A' ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.closest('button') ||
          target.closest('a') ||
          target.closest('[role="button"]') ||
          target.closest('.comment-pin') || // Don't place on existing pins
          target.closest('.comment-modal') // Don't place on modals
        ) {
          isHandlingClick.current = false;
          return;
        }

        // Locate primary scrollable container (domain-specific)
        const mains = Array.from(document.querySelectorAll('main'));

        let scrollableEl: HTMLElement | Window | null = null;
        for (const main of mains) {
          const descendants = Array.from(main.querySelectorAll('*')) as HTMLElement[];
          for (const el of descendants) {
            const style = window.getComputedStyle(el);
            const overflowY = style.overflowY || style.overflow;
            const isScrollable = el.scrollHeight > el.clientHeight + 50;
            if (isScrollable && /(auto|scroll)/.test(overflowY || '')) {
              scrollableEl = el;
              break;
            }
          }
          if (scrollableEl) break;
        }

        if (!scrollableEl) {
          scrollableEl =
            (document.querySelector('main[class*="overflow-auto"]') as HTMLElement) ||
            (document.querySelector('main[class*="overflow-y-auto"]') as HTMLElement) ||
            window;
        }

        const getScrollY = () => {
          if (scrollableEl instanceof Window) return window.scrollY;

          if (scrollableEl instanceof HTMLElement) {
            const elScroll = scrollableEl.scrollTop;
            const docScroll = document.documentElement.scrollTop || window.scrollY;
            return elScroll > 0 ? elScroll : docScroll;
          }

          return document.documentElement.scrollTop || window.scrollY;
        };

        // Reset flag inside RAF after pin placement completes, not on a fixed timer
        requestAnimationFrame(async () => {
          try {
            const currentScrollY = getScrollY();
            const x = event.clientX;
            const y = event.clientY;
            await addCommentPin(x, y, currentScrollY);
          } finally {
            isHandlingClick.current = false;
          }
        });
      } catch {
        isHandlingClick.current = false;
      }
    };

    // Add click listener
    document.addEventListener('click', handleClick, true); // Use capture phase

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [isCommentModeActive, addCommentPin]);

  // Handle ESC key to cancel comment mode
  useEffect(() => {
    if (!isCommentModeActive) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        deactivateCommentMode();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isCommentModeActive, deactivateCommentMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Reset cursor
      document.body.style.cursor = '';
    };
  }, []);

  const contextValue: CommentModeContextState = {
    isCommentModeActive,
    activateCommentMode,
    deactivateCommentMode,
    toggleCommentMode,
    commentPins,
    addCommentPin,
    removeCommentPin,
    resolveCommentPin,
    activePinId,
    setActivePinId,
  };

  return (
    <CommentModeContext.Provider value={contextValue}>
      {children}
    </CommentModeContext.Provider>
  );
};

/**
 * Hook to access comment mode context
 */
export const useCommentMode = (): CommentModeContextState => {
  const context = useContext(CommentModeContext);
  if (context === undefined) {
    throw new Error('useCommentMode must be used within a CommentModeProvider');
  }
  return context;
};
